import Subtitle from 'components/TextComps/Subtitle';
import './PaymentForm.scss';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCheckoutPricing, useRecurly } from '@recurly/react-recurly';
import { NonItemAdjustment, RecurlyError } from '@recurly/recurly-js';
import { PaymentDetails } from './PaymentDetails/PaymentDetails';
import { useServices, useStores } from 'index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { useCartStore } from 'stores/Webshop/CartStore';
import { CartItem } from 'types/cart';
import { clearCartFromLocalStorage } from 'utilities/cartLocalStorageHelper';
import { OrderDetails } from 'types/pricing';

export const PaymentForm = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const webshopService = useServices().webshopService;
  const profileService = useServices().profileService;
  const { data: isPremium, isPending: isPremiumPending } = useQuery({
    queryKey: ['isUserMember'],
    queryFn: profileService.checkIfUserIsMember,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  const notificationsStore = useStores().notificationStore;

  const recurly = useRecurly();
  const formRef = useRef<HTMLFormElement>(null);
  const itemsInCart: CartItem[] = useCartStore((state) => state.items);
  const total = useCartStore((state) => state.total);
  const pricingFormState = useCartStore((state) => state.form);
  const autoRenew = useCartStore((state) => state.autoRenew);
  const discount = useCartStore((state) => state.discount);
  const setDiscount = useCartStore((state) => state.setDiscount);
  const handleSetTaxRate = useCartStore((state) => state.setTax);
  const handleSetTotal = useCartStore((state) => state.setTotal);
  const setOrderCompletionDate = useCartStore((state) => state.setOrderCompletionDate);
  const setOrderEmail = useCartStore((state) => state.setOrderEmail);
  const setInvoiceNumber = useCartStore((state) => state.setInvoiceNumber);
  const setActiveStep = useCartStore((state) => state.setActiveStep);
  const setPricingFormState = useCartStore((state) => state.setForm);
  const setAutoRenew = useCartStore((state) => state.setAutoRenew);

  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const [tokenId, setTokenId] = useState<string>('');

  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedSetRecurlyPlanError = useCallback((error: RecurlyError | null) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
    }
    errorTimeoutRef.current = setTimeout(() => {
      if (error && error.message.includes('UniqueCouponCode')) {
        notificationsStore.addToast('error', t('RecurlyCouponError', { ns: 'webshop' }), 'error');
      }
    }, 500);
  }, []);

  const [{ price }, setCheckoutPricing] = useCheckoutPricing(pricingFormState, debouncedSetRecurlyPlanError);

  const hasDiscount = (!isPremiumPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');

  const areRequiredFieldsFilled = () => {
    const { email, first_name, last_name, address1, address2, city, country, postal_code } = pricingFormState;
    return (
      email !== '' &&
      first_name !== '' &&
      last_name !== '' &&
      address1 !== '' &&
      address2 !== '' &&
      city !== '' &&
      country !== '' &&
      postal_code !== ''
    );
  };

  useEffect(() => {
    const subscriptions = pricingFormState.plan ? [{ plan: pricingFormState.plan }] : [];
    const address = {
      country: pricingFormState.country,
      postal_code: pricingFormState.postal_code,
      first_name: pricingFormState.first_name,
      last_name: pricingFormState.last_name,
    };
    const adjustments: NonItemAdjustment[] = itemsInCart
      .filter((item) => item.id !== 'premium')
      .map((item) => ({
        id: item.id,
        amount: hasDiscount ? item.price * 0.5 : item.price,
        taxExempt: false,
        taxCode: 'digital',
        quantity: 1,
      }));

    setCheckoutPricing({ ...pricingFormState, address, subscriptions, adjustments });
  }, [pricingFormState, itemsInCart, hasDiscount, setCheckoutPricing, debouncedSetRecurlyPlanError]);

  useEffect(() => {
    const totalPrice = parseFloat(price?.now?.total) || 0;
    if (totalPrice && totalPrice !== total) {
      handleSetTaxRate(parseFloat(price?.now?.taxes) || 0);
      handleSetTotal(totalPrice);
    }
  }, [price]);

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  const onRecurlyValidationError = (err: RecurlyError) => {
    if (err.fields) {
      const errorMessage = `${err.message}. ${t('TheFollowingFieldsAppearToBeInvalid', { ns: 'purchaseMembership' })} ${err.fields.join(', ')}`;
      notificationsStore.addToast('error', getErrorMessage(errorMessage), 'error');
    } else if (err && err.message) {
      notificationsStore.addToast('error', getErrorMessage(err.message), 'error');
    }
  };

  const onPaymentProcessSuccess = async (orderDetails: OrderDetails) => {
    setOrderCompletionDate(orderDetails.closedAt);
    setOrderEmail(orderDetails.email);
    setInvoiceNumber(orderDetails.invoiceNumber);
    setActiveStep(2);
    clearCartFromLocalStorage();
    setProcessingPayment(false);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['isUserMember'] }),
      queryClient.invalidateQueries({ queryKey: ['shopCertificationTests'] }),
    ]);
  };

  const handleToken = useMutation({
    mutationFn: (tokenId: string) =>
      webshopService.tokenHandler(
        tokenId,
        pricingFormState.email,
        pricingFormState.plan,
        price?.currency.code || '',
        discount.couponCode,
        itemsInCart.map((item) => item.id).filter((id) => id !== 'premium'),
        itemsInCart.some((item) => item.id === 'premium')
      ),
    onSuccess: async (response) => {
      if (response.status === 'tokenReturned') {
        handleThreeDSecure(response.token);
      } else if (response.status === 'success') {
        await onPaymentProcessSuccess(response.orderDetails);
      } else {
        const errorMessage = response.errorMessage.detail;
        notificationsStore.addToast('error', getErrorMessage(errorMessage), 'error');
        setProcessingPayment(false);
      }
    },
    onError: (ex) => {
      notificationsStore.addToast('error', getErrorMessage(ex), 'error');
      setProcessingPayment(false);
    },
  });

  const handleSubmit = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    }

    setProcessingPayment(true);

    if (discount?.couponCode) {
      setDiscount({ ...discount, currencySymbol: price?.currency.symbol, discount: price.now.discount });
    }

    formRef.current &&
      recurly.token(formRef.current, async (err, token) => {
        if (err) {
          onRecurlyValidationError(err);
          setProcessingPayment(false);
        } else {
          setTokenId(token.id);
          handleToken.mutate(token.id);
        }
      });
  };

  const handleThreeDSecure = (actionTokenId: string) => {
    const threeDSecure = recurly.Risk().ThreeDSecure({ actionTokenId: actionTokenId });

    threeDSecure.on('error', (err) => {
      notificationsStore.addToast('error', err.message, 'error');
      setProcessingPayment(false);
    });

    threeDSecure.on('token', (actionToken) => {
      sendThreeDSecureRequest(
        tokenId,
        pricingFormState.email,
        pricingFormState.plan,
        price.currency.code,
        pricingFormState.coupon,
        actionToken.id,
        itemsInCart.map((item) => item.id).filter((id) => id !== 'premium'),
        itemsInCart.some((item) => item.id === 'premium')
      );
    });

    threeDSecure.attach(document.body);
  };

  const sendThreeDSecureRequest = async (
    tokenId: string,
    email: string,
    planCode: string,
    currency: string,
    couponCode: string,
    threeDSecureTokenId: string,
    productIds: string[],
    includeSubscription: boolean
  ) => {
    try {
      const response = await webshopService.sendThreeDSecureRequest(
        tokenId,
        email,
        planCode,
        currency,
        couponCode,
        threeDSecureTokenId,
        productIds,
        includeSubscription
      );

      if (response.data && response.status !== 200) {
        notificationsStore.addToast('error', response.data.detail, 'error');
      } else if (response.data && response.data.data.isError) {
        notificationsStore.addToast('error', t('PurchaseSubscriptionError', { ns: 'purchaseMembership' }), 'error');
      } else if (response.data.data) {
        await onPaymentProcessSuccess(response.data.data);
      }
    } catch (ex) {
      notificationsStore.addToast('error', getErrorMessage(ex), 'error');
    }
    setProcessingPayment(false);
  };

  return (
    <div className='payment-form-wrapper'>
      <Subtitle>
        <>{t('PaymentDetails', { ns: 'webshop' })}</>
      </Subtitle>
      <Form
        formRef={formRef}
        handleSubmit={handleSubmit}
        pricingFormState={pricingFormState}
        handlePricingFormStateChange={setPricingFormState}
        processingPayment={processingPayment}
        isButtonEnabled={areRequiredFieldsFilled() && !processingPayment}
        isSubscriptionInCart={itemsInCart.some((item) => item.id === 'premium')}
        autoRenew={autoRenew}
        setDiscount={setDiscount}
        handleAutoRenew={setAutoRenew}
      />
    </div>
  );
};

const Form = ({
  formRef,
  handleSubmit,
  pricingFormState,
  handlePricingFormStateChange,
  isSubscriptionInCart,
  autoRenew,
  processingPayment,
  isButtonEnabled,
  setDiscount,
  handleAutoRenew,
}) => {
  return (
    <form className='payment-form' ref={formRef} onSubmit={(e) => handleSubmit(e)}>
      <PaymentDetails
        pricingFormState={pricingFormState}
        setPricingFormState={(e) => handlePricingFormStateChange(e)}
        processingPayment={processingPayment}
        isButtonEnabled={isButtonEnabled}
        isSubscriptionInCart={isSubscriptionInCart}
        autoRenew={autoRenew}
        setDiscount={setDiscount}
        handleAutoRenew={(e) => handleAutoRenew(e)}
      />
    </form>
  );
};
